import { toDiscountTypeCode, type InquiryDiscountTypeCode } from "./discount-type";
import type { InquiryFormData } from "./types";
import { coerceCecManagedValue } from "./cec-managed";

type InquiryApiShapeExtras = {
  subjectIds?: string[] | null;
  isProcessingStatus?: boolean;
  inquiryStatusCode?: string;
  inquiry_status_code?: string;
  inquiryStage?: string;
  statusStartDate?: string;
  stageStartDate?: string;
};

type InquiryFormDataWithApiExtras = InquiryFormData & InquiryApiShapeExtras;

export interface InquiryApiRequestPayload {
  service_id: string;
  program_id: string;
  student_id: number;
  inquirer_id: number;
  taken_by_id: string;
  referred_by_id: string;
  source_id: string;
  family_id: string | null;
  inquiryDate: string;
  relationship_id?: string;
  test_id?: string;
  subject_ids?: string[];
  aeFee?: number;
  discountType?: InquiryDiscountTypeCode;
  discountValue?: number;
  discount_reason_id?: string;
  discountReasonOther?: string;
  referredByOther?: string;
  standardHourlyTuitionQuoted?: number;
  privateHourlyTuitionQuoted?: number;
  regFee?: number;
  notes?: string;
  cecManaged?: boolean;
  isProcessingStatus?: boolean;
  inquiry_status_code?: string;
  statusStartDate?: string;
  inquiryStage?: string;
  stageStartDate?: string;
  id?: string;
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const normalized = value.trim();
    return normalized.length ? normalized : undefined;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return `${value}`;
  }

  return undefined;
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const requireString = (value: unknown, fieldName: string): string => {
  const normalized = toTrimmedString(value);
  if (!normalized) {
    throw new Error(`Missing required inquiry payload field: ${fieldName}`);
  }
  return normalized;
};

const requireInteger = (value: unknown, fieldName: string): number => {
  const normalized = toNumber(value);
  if (normalized === undefined) {
    throw new Error(`Missing required inquiry payload field: ${fieldName}`);
  }
  return Math.trunc(normalized);
};

const toNullableString = (value: unknown): string | null | undefined => {
  if (value === null) {
    return null;
  }

  return toTrimmedString(value);
};

const toSubjectIds = (payload: InquiryFormDataWithApiExtras): string[] | undefined => {
  const fromSubjectIds = Array.isArray(payload.subjectIds)
    ? payload.subjectIds.filter(
        (subjectId): subjectId is string => typeof subjectId === "string" && subjectId.length > 0
      )
    : [];

  if (fromSubjectIds.length) {
    return fromSubjectIds;
  }

  const fallbackSubjectId = toTrimmedString(payload.subjectId);
  if (fallbackSubjectId) {
    return [fallbackSubjectId];
  }

  return undefined;
};

export const serializeInquiryForApi = (
  payload: InquiryFormData,
  { includeRelationshipId = true, includeId = false }: { includeRelationshipId?: boolean; includeId?: boolean } = {}
): InquiryApiRequestPayload => {
  const normalizedPayload = payload as InquiryFormDataWithApiExtras;
  const coercedCecManaged = coerceCecManagedValue(normalizedPayload.cecManaged);

  const requestPayload: InquiryApiRequestPayload = {
    service_id: requireString(normalizedPayload.serviceId, "service_id"),
    program_id: requireString(normalizedPayload.programId, "program_id"),
    student_id: requireInteger(normalizedPayload.studentId ?? normalizedPayload.student?.id, "student_id"),
    inquirer_id: requireInteger(normalizedPayload.inquirerId ?? normalizedPayload.inquirer?.id, "inquirer_id"),
    taken_by_id: requireString(normalizedPayload.takenById ?? normalizedPayload.takenBy?.id, "taken_by_id"),
    referred_by_id: requireString(normalizedPayload.referredById, "referred_by_id"),
    source_id: requireString(normalizedPayload.sourceId, "source_id"),
    family_id: toNullableString(normalizedPayload.familyId ?? normalizedPayload.family?.id) ?? null,
    inquiryDate: requireString(normalizedPayload.inquiryDate, "inquiryDate"),
    cecManaged: coercedCecManaged
  };

  if (includeRelationshipId) {
    requestPayload.relationship_id = requireString(normalizedPayload.relationshipId, "relationship_id");
  }

  if (includeId) {
    const id = toTrimmedString(normalizedPayload.id);
    if (id) {
      requestPayload.id = id;
    }
  }

  const testId = toTrimmedString(normalizedPayload.testId);
  if (testId) {
    requestPayload.test_id = testId;
  }

  const subjectIds = toSubjectIds(normalizedPayload);
  if (subjectIds?.length) {
    requestPayload.subject_ids = subjectIds;
  }

  const aeFee = toNumber(normalizedPayload.aeFee?.baseFee);
  if (aeFee !== undefined) {
    requestPayload.aeFee = aeFee;
  }

  const discountType = toDiscountTypeCode(normalizedPayload.aeFee?.discountType);
  if (discountType) {
    requestPayload.discountType = discountType;
  }

  const discountValue = toNumber(normalizedPayload.aeFee?.discountValue);
  if (discountValue !== undefined) {
    requestPayload.discountValue = discountValue;
  }

  const discountReasonId = toTrimmedString(normalizedPayload.aeFee?.discountReasonId);
  if (discountReasonId) {
    requestPayload.discount_reason_id = discountReasonId;
  }

  const discountReasonOther = toTrimmedString(normalizedPayload.aeFee?.discountReasonOther);
  if (discountReasonOther) {
    requestPayload.discountReasonOther = discountReasonOther;
  }

  const referredByOther = toTrimmedString(normalizedPayload.referredByOther);
  if (referredByOther) {
    requestPayload.referredByOther = referredByOther;
  }

  const standardHourlyTuitionQuoted = toNumber(normalizedPayload.standardHourlyTuitionQuoted);
  if (standardHourlyTuitionQuoted !== undefined) {
    requestPayload.standardHourlyTuitionQuoted = standardHourlyTuitionQuoted;
  }

  const privateHourlyTuitionQuoted = toNumber(normalizedPayload.privateHourlyTuitionQuoted);
  if (privateHourlyTuitionQuoted !== undefined) {
    requestPayload.privateHourlyTuitionQuoted = privateHourlyTuitionQuoted;
  }

  const regFee = toNumber(normalizedPayload.regFee);
  if (regFee !== undefined) {
    requestPayload.regFee = regFee;
  }

  if (typeof normalizedPayload.notes === "string") {
    requestPayload.notes = normalizedPayload.notes;
  }

  if (typeof normalizedPayload.isProcessingStatus === "boolean") {
    requestPayload.isProcessingStatus = normalizedPayload.isProcessingStatus;
  }

  const inquiryStatusCode = toTrimmedString(
    normalizedPayload.inquiryStatusCode ?? normalizedPayload.inquiry_status_code
  );
  if (inquiryStatusCode) {
    requestPayload.inquiry_status_code = inquiryStatusCode;
  }

  const statusStartDate = toTrimmedString(normalizedPayload.statusStartDate);
  if (statusStartDate) {
    requestPayload.statusStartDate = statusStartDate;
  }

  const inquiryStage = toTrimmedString(normalizedPayload.inquiryStage);
  if (inquiryStage) {
    requestPayload.inquiryStage = inquiryStage;
  }

  const stageStartDate = toTrimmedString(normalizedPayload.stageStartDate);
  if (stageStartDate) {
    requestPayload.stageStartDate = stageStartDate;
  }

  return requestPayload;
};
